/*
* === AdminDashboard.js (Option 2 - Fixed Radar Chart) ===
*/
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { 
    Container, Typography, Grid, Card, CardContent, Box, 
    Alert, List, ListItem, ListItemText, ListItemAvatar, Avatar, Chip, 
    Divider, CardHeader, Paper, Button, TextField, ListItemButton,
    IconButton, Skeleton
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom'; 

// Icons
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// CHARTS (Visual Set)
import { 
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer, XAxis, YAxis, 
    CartesianGrid, Legend, AreaChart, Area, Radar, RadarChart, PolarGrid, 
    PolarAngleAxis, PolarRadiusAxis, BarChart, Bar
} from 'recharts';

import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import StatCard from '../../components/StatCard';
import { DashboardSkeleton, ChartSkeleton } from '../../components/Skeletons';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF19A3'];

const NoData = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
        <Typography>No data available</Typography>
    </Box>
);

// --- INTERVIEW SCHEDULER MODAL ---
const InterviewSchedulerModal = ({ open, onClose, token }) => {
    const [slots, setSlots] = useState([]);
    const [newSlot, setNewSlot] = useState({ date: '', startTime: '', endTime: '', link: '' });
    const [loading, setLoading] = useState(false);

    const fetchSlots = async () => {
        try {
            const res = await api.get('/admin/interviews/my-slots', { headers: { Authorization: `Bearer ${token}` } });
            setSlots(res.data);
        } catch (e) { console.error("Error fetching slots"); }
    };

    useEffect(() => { if (open) fetchSlots(); }, [open]);

    const handleAddSlot = async () => {
        if (!newSlot.date || !newSlot.startTime || !newSlot.endTime) return alert("Please fill all time fields.");
        setLoading(true);
        try {
            const startDateTime = `${newSlot.date}T${newSlot.startTime}:00`;
            const endDateTime = `${newSlot.date}T${newSlot.endTime}:00`;
            
            await api.post('/admin/interviews/slots', {
                startTime: startDateTime,
                endTime: endDateTime,
                meetingLink: newSlot.link
            }, { headers: { Authorization: `Bearer ${token}` } });
            
            setNewSlot({ ...newSlot, startTime: '', endTime: '' }); 
            fetchSlots();
        } catch (e) { alert("Failed to add slot."); }
        finally { setLoading(false); }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/admin/interviews/slots/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            fetchSlots();
        } catch (e) { alert("Failed to delete slot."); }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Manage Interview Availability</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
                    <Grid item xs={12} sm={3}><TextField type="date" label="Date" InputLabelProps={{ shrink: true }} fullWidth size="small" value={newSlot.date} onChange={e => setNewSlot({...newSlot, date: e.target.value})} /></Grid>
                    <Grid item xs={6} sm={2}><TextField type="time" label="Start" InputLabelProps={{ shrink: true }} fullWidth size="small" value={newSlot.startTime} onChange={e => setNewSlot({...newSlot, startTime: e.target.value})} /></Grid>
                    <Grid item xs={6} sm={2}><TextField type="time" label="End" InputLabelProps={{ shrink: true }} fullWidth size="small" value={newSlot.endTime} onChange={e => setNewSlot({...newSlot, endTime: e.target.value})} /></Grid>
                    <Grid item xs={12} sm={3}><TextField label="Link (Optional)" fullWidth size="small" value={newSlot.link} onChange={e => setNewSlot({...newSlot, link: e.target.value})} /></Grid>
                    <Grid item xs={12} sm={2}><Button variant="contained" fullWidth onClick={handleAddSlot} disabled={loading} startIcon={<AddIcon />}>Add</Button></Grid>
                </Grid>
                <Typography variant="subtitle2" gutterBottom>Your Slots</Typography>
                <List dense sx={{ bgcolor: 'background.paper', border: '1px solid #eee', borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                    {slots.length === 0 && <ListItem><ListItemText primary="No slots added." /></ListItem>}
                    {slots.map(slot => (
                        <ListItem key={slot.Id} divider secondaryAction={<IconButton edge="end" onClick={() => handleDelete(slot.Id)} disabled={slot.Status === 'Booked'}><DeleteIcon color={slot.Status === 'Booked' ? 'disabled' : 'error'} /></IconButton>}>
                            <ListItemText primary={`${new Date(slot.StartTime).toLocaleDateString()} | ${new Date(slot.StartTime).toLocaleTimeString()}`} secondary={slot.Status === 'Booked' ? `Booked by: ${slot.ApplicantName}` : 'Available'} />
                            {slot.Status === 'Booked' && <Chip label="BOOKED" color="success" size="small" sx={{ mr: 2 }} />}
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
        </Dialog>
    );
};

const AdminDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { token, user } = useAuth(); 
    const navigate = useNavigate();

    const [isSchedulerOpen, setIsSchedulerOpen] = useState(false);
    const [dateFilter, setDateFilter] = useState({ startDate: '', endDate: '' });

    const fetchData = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const params = {};
            if (dateFilter.startDate) params.startDate = dateFilter.startDate;
            if (dateFilter.endDate) params.endDate = dateFilter.endDate;

            const response = await api.get('/admin/dashboard-data', { 
                headers: { Authorization: `Bearer ${token}` },
                params
            });
            setDashboardData(response.data);
        } catch (err) { setError(`Failed to fetch dashboard data.`); } 
        finally { setLoading(false); }
    }, [token, dateFilter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleStatusClick = (data) => { if (data && data.name) navigate(`/admin/search?status=${data.name}`); };
    const handleTrendClick = () => navigate('/admin/search');

    const chartData = useMemo(() => {
        const charts = dashboardData?.charts;
        const trendRawData = charts?.submissionsTrend || [];
        const statusDist = charts?.statusDistribution?.map(item => ({ name: item.Status, value: item.count })) || [];
        
        statusDist.sort((a, b) => b.value - a.value);

        // NEW: Document Health Logic
        const docHealth = charts?.documentHealth || [
            { name: 'Ready (Verified)', value: 0 },
            { name: 'Pending Review', value: 0 },
            { name: 'Incomplete', value: 0 }
        ];

        // NEW: Financial Need Funnel (Replacing Radar)
        const funnelData = [
            { bracket: '< R150k', count: 0 },
            { bracket: 'R150k - R350k', count: 0 },
            { bracket: 'R350k - R600k', count: 0 },
            { bracket: '> R600k', count: 0 }
        ].map(item => {
            const apiItem = charts?.demographics?.financial?.find(f => f.incomeBracket === item.bracket);
            return { ...item, count: apiItem ? apiItem.count : 0 };
        });
        
        // Create a map for quick lookup of API results (e.g., {"< R150k": 1})
        const apiFinancialMap = new Map();
        if (charts?.demographics?.financial) {
            charts.demographics.financial.forEach(item => {
                // Assuming backend returns buckets like "0-150000" or label "Low Income"
                // For robustness, we map known backend keys or just use what is returned if it matches
                apiFinancialMap.set(item.incomeBracket, item.count);
            });
        }

        // Fallback: If API returned absolutely no matches (e.g. raw numbers), map raw items if > 2
        // But the above ensures we always have 4 points (Square shape)
        
        return {
            trendData: (charts?.submissionsTrend || []).map(d => ({ 
            date: new Date(d.date).toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' }), 
            Applications: d.count 
            })),
            statusDistData: (charts?.statusDistribution || []).map(item => ({ name: item.Status, value: item.count })),
            genderData: (charts?.demographics?.gender || []).map(item => ({ name: item.Gender, value: item.count })),
            funnelData,
            docHealth
        };
    }, [dashboardData]);

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold"><Skeleton width="40%" /></Typography>
                <DashboardSkeleton />
                <ChartSkeleton />
            </Container>
        );
    }

    if (error) return <Container sx={{mt: 4}}><Alert severity="error">{error}</Alert></Container>;
    
    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" fontWeight="bold">
                    {user.role === 'SuperUser' ? 'System Overview' : 'Review Dashboard'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, bgcolor: 'background.paper', p: 1, borderRadius: 2 }}>
                    <FilterAltIcon color="action" sx={{ my: 'auto', ml: 1 }} />
                    <TextField label="Start Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={dateFilter.startDate} onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))} />
                    <TextField label="End Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={dateFilter.endDate} onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))} />
                </Box>
            </Box>

            <Paper sx={{ p: 2, mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }} elevation={1}>
                <Button variant="outlined" startIcon={<PlayArrowIcon />} onClick={() => navigate('/admin/search?filter=assigned_to_me')}>My Tasks</Button>
                <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={() => navigate('/admin/users?action=create')}>Invite User</Button>
                <Button variant="outlined" startIcon={<AddCircleIcon />} onClick={() => navigate('/admin/bursaries?action=create')}>Create Bursary</Button>
                <Button variant="contained" color="secondary" startIcon={<EventIcon />} onClick={() => setIsSchedulerOpen(true)}>Interview Slots</Button>
            </Paper>

            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard title="Applications" value={dashboardData?.stats?.totalApplications || 0} icon={<AssignmentIndIcon />} color="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard title="Action Required" value={dashboardData?.stats?.myAssignedQueue || 0} icon={<HourglassEmptyIcon />} color="linear-gradient(135deg, #ff9a9e 0%, #fecfef 99%, #fecfef 100%)" textColor="#333" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard title="New Today" value={dashboardData?.stats?.newToday || 0} icon={<ShowChartIcon />} color="linear-gradient(120deg, #89f7fe 0%, #66a6ff 100%)" textColor="#333" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard title="Under Review" value={dashboardData?.stats?.underReview || 0} icon={<FolderOpenIcon />} color="linear-gradient(to top, #cfd9df 0%, #e2ebf0 100%)" textColor="#333" />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} lg={8}>
                    <Grid container spacing={3}>
                        {/* CHART 1: Gradient Area Chart (Submissions) */}
                        <Grid item xs={12}>
                            <Card>
                                <CardHeader title="Incoming Application Flow" />
                                <CardContent>
                                    <Box sx={{ height: 350 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            {chartData.trendData.length > 0 ? (
                                                <AreaChart data={chartData.trendData} onClick={handleTrendClick} style={{ cursor: 'pointer' }}>
                                                    <defs>
                                                        <linearGradient id="colorApps" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                                                            <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                                                        </linearGradient>
                                                    </defs>
                                                    <XAxis dataKey="date" />
                                                    <YAxis />
                                                    <Tooltip />
                                                    <Area type="monotone" dataKey="Applications" stroke="#8884d8" fillOpacity={1} fill="url(#colorApps)" />
                                                </AreaChart>
                                            ) : <NoData />}
                                        </ResponsiveContainer>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* CHART 2: Funnel-Style Bar Chart (Sorted Status) */}
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardHeader title="Pipeline Overview" subheader="Sorted by Volume" />
                                <CardContent>
                                    <Box sx={{ height: 300 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart layout="vertical" data={chartData.statusDistData} onClick={handleStatusClick} style={{ cursor: 'pointer' }}>
                                                <XAxis type="number" hide />
                                                <YAxis dataKey="name" type="category" width={100} />
                                                <Tooltip />
                                                <Bar dataKey="value" fill="#82ca9d" barSize={20} radius={[0, 10, 10, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* CHART 3: Radar Chart (Financials) */}
                        {(user.role === 'SuperUser' || user.role === 'Admin') && (
                            <Grid item xs={12} md={6}>
                                <Card>
                                    <CardHeader title="Financial Need Funnel" subheader="Applicant Household Income" />
                                    <CardContent>
                                        <Box sx={{ height: 300 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={chartData.funnelData}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="bracket" tick={{fontSize: 12}} />
                                                    <YAxis />
                                                    <Tooltip />
                                                    <Bar dataKey="count" fill="#667eea" radius={[10, 10, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </Grid>

                <Grid item xs={12} lg={4}>
                    <Grid container spacing={3}>
                        {/* CHART 4: Donut Chart (Gender) */}
                        <Grid item xs={12}>
                            <Card>
                                <CardHeader title="Demographics" />
                                <CardContent>
                                    <Box sx={{ height: 280 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={chartData.genderData} innerRadius={60} outerRadius={80} fill="#8884d8" paddingAngle={5} dataKey="value">
                                                    {chartData.genderData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                                                </Pie>
                                                <Tooltip />
                                                <Legend verticalAlign="bottom" height={36}/>
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Card>
                                <CardHeader title="Latest Updates" />
                                <List sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
                                    {dashboardData?.recentActivity?.map((activity, index) => (
                                        <React.Fragment key={index}>
                                            <ListItemButton component={Link} to={`/admin/review/${activity.Id}`}>
                                                <ListItemAvatar>
                                                    <Avatar sx={{ bgcolor: activity.Status === 'Submitted' ? 'primary.main' : 'grey.300' }}>
                                                        <FolderOpenIcon />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText primary={activity.FullName} secondary={new Date(activity.SubmittedAt).toLocaleDateString()} />
                                                <Chip label={activity.Status} size="small" variant="outlined" />
                                            </ListItemButton>
                                            <Divider variant="inset" component="li" />
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            
            <InterviewSchedulerModal open={isSchedulerOpen} onClose={() => setIsSchedulerOpen(false)} token={token} />
        </Container>
    );
};

export default AdminDashboard;